import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import {
  datetimeLocalToIso,
  formatPricingDate,
  getPriceScheduleStatusInfo,
  isoToDatetimeLocal,
  validateScheduleDeactivatesAt
} from 'src/app/services/product-pricing.util';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-price-history-dialog',
  templateUrl: './price-history-dialog.component.html',
  styleUrls: ['./price-history-dialog.component.scss']
})
export class PriceHistoryDialogComponent implements OnInit {

  @Input() storeId: string = '';
  @Input() productId: string = '';
  @Input() productName: string = '';
  @Input() storeCurrency: string = 'DKK';

  schedules: any[] = [];
  loading = false;
  savingScheduleId: string | null = null;
  editingActiveEndId: string | null = null;
  editActiveDeactivatesAt = '';
  editActiveEndError = '';

  constructor(
    private dialogRef: NbDialogRef<PriceHistoryDialogComponent>,
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.storeId && this.productId) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    this.loading = true;
    this.ecommerceService.getProductPriceScheduleHistory(this.storeId, this.productId).subscribe({
      next: (res: any) => {
        const data = res?.response?.data ?? res?.data;
        this.schedules = Array.isArray(data)
          ? data
          : (data?.priceSchedule || data?.schedules || []);
        this.loading = false;
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Failed to load price history');
        this.loading = false;
      }
    });
  }

  formatDate(value: string): string {
    return formatPricingDate(value);
  }

  getStatusInfo(status: string): { label: string; badgeClass: string } {
    return getPriceScheduleStatusInfo(status);
  }

  formatPrice(price: number): string {
    if (price == null || !Number.isFinite(Number(price))) {
      return '—';
    }
    return `${Number(price).toFixed(2)} ${this.storeCurrency || 'DKK'}`;
  }

  startEditActiveEnd(schedule: any): void {
    this.editingActiveEndId = schedule._id;
    this.editActiveDeactivatesAt = isoToDatetimeLocal(schedule.deactivatesAt);
    this.editActiveEndError = '';
  }

  cancelEditActiveEnd(): void {
    this.editingActiveEndId = null;
    this.editActiveDeactivatesAt = '';
    this.editActiveEndError = '';
  }

  clearActiveEnd(schedule: any): void {
    if (!schedule?._id) {
      return;
    }
    this.savingScheduleId = schedule._id;
    this.ecommerceService.updateProductPriceSchedule(this.storeId, this.productId, schedule._id, {
      deactivatesAt: null
    }).subscribe({
      next: () => {
        this.savingScheduleId = null;
        this.cancelEditActiveEnd();
        this.toastr.success('Scheduled end cleared');
        this.loadHistory();
      },
      error: (err: any) => {
        this.savingScheduleId = null;
        this.toastr.error(err?.error?.response?.message || 'Failed to clear end date');
      }
    });
  }

  saveActiveEnd(schedule: any): void {
    this.editActiveEndError = validateScheduleDeactivatesAt(this.editActiveDeactivatesAt, isoToDatetimeLocal(schedule.activatesAt)) || '';
    if (this.editActiveEndError) {
      return;
    }
    if (!schedule?._id) {
      return;
    }

    const payload: any = {};
    if (this.editActiveDeactivatesAt) {
      payload.deactivatesAt = datetimeLocalToIso(this.editActiveDeactivatesAt);
    } else {
      payload.deactivatesAt = null;
    }

    this.savingScheduleId = schedule._id;
    this.ecommerceService.updateProductPriceSchedule(this.storeId, this.productId, schedule._id, payload).subscribe({
      next: () => {
        this.savingScheduleId = null;
        this.cancelEditActiveEnd();
        this.toastr.success('End date updated');
        this.loadHistory();
      },
      error: (err: any) => {
        this.savingScheduleId = null;
        this.toastr.error(err?.error?.response?.message || 'Failed to update end date');
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
